// R5 (hook) — hard alignment. Mirrors the reference mockup's
// `applyAlignment()` (docs/mockups/side-by-side.html): walk the anchors
// top-down, and for each one measure its CURRENT live top in both panes
// (`getBoundingClientRect`, so margins already injected by earlier anchors in
// this same pass have already propagated through normal flow) and push
// whichever pane's anchor is higher down by the delta via `marginTop`. This
// live, iterative, self-correcting pass is what makes paired anchors sit at
// equal heights and the panes become height-identical for a locked 1:1 scroll.
//
// This is the reader's DEFAULT mode as well as the editor's: `reading-view.tsx`
// resolves it as `getPref("syncMode", "hard")`, and only `?sync=interpolated`
// turns it off (SPEC "Sync anchors").
//
// This is deliberately NOT computed from a single static measurement of
// segment heights (`buildSyncSegments`/`computeHardAlignPadding`, still used
// by `useSyncScroll` for interpolation and kept exported for the phase-7
// anchor editor): margins injected by earlier anchors shift every later
// block, and the live re-measurement lets each correction observe the
// previous ones through normal flow. Sub-block anchors snap to their
// containing block's top — whitespace can't be inserted mid-paragraph
// (interpolated mode honors them exactly) — and a block drives/receives at
// most ONE correction: consecutive anchors resolving to the same containing
// block on either side (sub-block anchors sharing one paragraph) are skipped,
// since
// re-correcting the shared block would just compound margins onto it and
// shove the whole pane down by the sum of every delta.
//
// The margin injection is a legitimate layout side-effect — it mutates style
// on the rendered block elements via refs and fully resets them on
// cleanup / re-run.

import { useLayoutEffect, useRef, type RefObject } from "react";
import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";
import type { DocumentModel } from "@bindery/shared/corpus/document";

const ECHO_EPSILON = 1.5;

interface UseHardAlignArgs {
  retellingRef: RefObject<HTMLDivElement | null>;
  originalRef: RefObject<HTMLDivElement | null>;
  retellingModel: DocumentModel;
  originalModel: DocumentModel | null;
  anchors: readonly CorpusAnchorPair[];
  /** Both panes visible AND hard mode. */
  enabled: boolean;
  retellingFontSize: number;
  originalFontSize: number;
  /** Opaque token; changing it re-runs the pass (see useSyncScroll). */
  layoutKey: string;
}

/**
 * Reset any injected margins so the next measure sees natural layout. Scoped
 * to the article's direct children (the blocks themselves): every text run
 * span also carries `data-start`, so an unscoped `[data-start]` selector would
 * sweep hundreds–thousands of run spans on every measure (each ResizeObserver
 * tick / font event) for no reason — only block-level elements ever get a
 * margin injected.
 */
function clearMargins(container: HTMLElement): void {
  const els = container.querySelectorAll<HTMLElement>(
    ":scope > article > [data-start]",
  );
  for (const el of els) {
    el.style.marginTop = "";
    el.style.marginBottom = "";
  }
}

/** Precise selector for a block's rendered element (runs also carry data-start,
 *  so match the id when present and the exact start otherwise). */
function blockEl(
  container: HTMLElement,
  block: { id?: string; start: number },
): HTMLElement | null {
  const selector = block.id
    ? `[data-block-id="${block.id}"]`
    : `[data-start="${String(block.start)}"]`;
  return container.querySelector<HTMLElement>(selector);
}

/** DOM element that owns an original char offset (snap to containing block). */
function originalBlockEl(
  container: HTMLElement,
  model: DocumentModel,
  offset: number,
): HTMLElement | null {
  const block =
    model.blocks.find((b) => offset < b.end) ??
    model.blocks[model.blocks.length - 1];
  return block ? blockEl(container, block) : null;
}

export function useHardAlign({
  retellingRef,
  originalRef,
  retellingModel,
  originalModel,
  anchors,
  enabled,
  retellingFontSize,
  originalFontSize,
  layoutKey,
}: UseHardAlignArgs): void {
  const expectedRef = useRef(new Map<HTMLElement, number>());

  useLayoutEffect(() => {
    const retelling = retellingRef.current;
    const original = originalRef.current;
    if (!enabled || !retelling || !original || !originalModel) return;

    // Stable useRef Map; local alias so cleanup closes over it (react-hooks lint).
    const expectedTops = expectedRef.current;

    const addMargin = (
      el: HTMLElement | null,
      px: number,
      bottom = false,
    ): void => {
      if (!el || px <= 0) return;
      const prop = bottom ? "marginBottom" : "marginTop";
      const current = parseFloat(el.style[prop] || "0");
      el.style[prop] = `${String(current + px)}px`;
    };

    // Live top of an element within its pane's scroll content — mirrors the
    // mockup's `topIn`. Reflects any margins already applied earlier in this
    // same pass, since `getBoundingClientRect` forces a fresh layout.
    const topIn = (pane: HTMLElement, el: HTMLElement): number =>
      el.getBoundingClientRect().top -
      pane.getBoundingClientRect().top +
      pane.scrollTop;

    const applyPadding = (): void => {
      clearMargins(retelling);
      clearMargins(original);

      // Top-down, one anchor at a time: measure LIVE tops (so earlier pushes
      // in this loop already propagate through normal flow to later
      // anchors), then push whichever pane's anchor is higher down to match.
      // Anchors are strictly monotonic by construction (docs/anchor-algorithm.md),
      // so no extra ordering/monotonicity guard is needed here — unlike
      // interpolation's `buildSyncSegments`, which also has to defend against
      // stale data since it drives every scroll frame.
      let prevRetEl: HTMLElement | null = null;
      let prevOrigEl: HTMLElement | null = null;
      for (const pair of anchors) {
        const retEl = retelling.querySelector<HTMLElement>(
          `[data-block-id="${pair.retelling.blockId}"]`,
        );
        if (!retEl) continue; // stale anchor — retelling block no longer exists
        const origEl = originalBlockEl(
          original,
          originalModel,
          pair.originalOffset,
        );
        if (!origEl) continue;

        // A block gets at most one correction: whitespace can't separate two
        // anchors inside the SAME block, and re-pushing a shared block (e.g.
        // sub-block anchors sharing one paragraph) would compound margins
        // onto it — the whole pane ends up shoved down by the sum of all
        // deltas. Anchors are monotonic, so same-block repeats are
        // consecutive and the previous corrected pair suffices. (Verse
        // originals are per-line blocks — scripts/ingest/verseLines.ts — so
        // this guard no longer suppresses verse corrections.)
        if (retEl === prevRetEl || origEl === prevOrigEl) continue;

        const delta = topIn(retelling, retEl) - topIn(original, origEl);
        if (delta > 0)
          addMargin(origEl, delta); // original higher → push it down
        else if (delta < 0) addMargin(retEl, -delta);
        prevRetEl = retEl;
        prevOrigEl = origEl;
      }

      // Tail sentinel: equalize the remaining content past the last anchor
      // (live, post-loop) so the panes become height-identical end-to-end —
      // needed for the 1:1 scroll lock (the mockup itself has no lock-critical
      // trailing content to equalize; the reader does).
      const lastRetBlock = retellingModel.blocks.at(-1);
      const lastOrigBlock = originalModel.blocks.at(-1);
      const lastRetEl = lastRetBlock ? blockEl(retelling, lastRetBlock) : null;
      const lastOrigEl = lastOrigBlock
        ? blockEl(original, lastOrigBlock)
        : null;
      if (lastRetEl && lastOrigEl) {
        const retBottom =
          topIn(retelling, lastRetEl) +
          lastRetEl.getBoundingClientRect().height;
        const origBottom =
          topIn(original, lastOrigEl) +
          lastOrigEl.getBoundingClientRect().height;
        const delta = retBottom - origBottom;
        if (delta > 0) addMargin(lastOrigEl, delta, true);
        else if (delta < 0) addMargin(lastRetEl, -delta, true);
      }
    };

    // 1:1 scroll lock — heights match after equalization. `behavior:
    // "instant"` overrides CSS `scroll-behavior`, so the follower's set
    // produces exactly one scroll event (see use-sync-scroll's syncFrom).
    // Clamp before recording: padded heights only equalize up to layout
    // rounding, so near the document bottom an unclamped assignment gets
    // clamped by the browser anyway, and the echoed event would then differ
    // from the recorded (unclamped) value enough to misread as a genuine
    // gesture and bounce the leader back.
    const mirror = (fromEl: HTMLElement, toEl: HTMLElement): void => {
      const maxScroll = toEl.scrollHeight - toEl.clientHeight;
      const next = Math.max(0, Math.min(fromEl.scrollTop, maxScroll));
      // Skip recording a no-op: it fires no echo to clear the entry, and a
      // stale one would swallow the follower's next genuine gesture event.
      if (Math.abs(toEl.scrollTop - next) > ECHO_EPSILON) {
        expectedTops.set(toEl, next);
      }
      toEl.scrollTo({ top: next, behavior: "instant" });
    };
    const onScroll = (ev: Event): void => {
      const el = ev.currentTarget as HTMLElement;
      const expected = expectedTops.get(el);
      if (
        expected !== undefined &&
        Math.abs(el.scrollTop - expected) <= ECHO_EPSILON
      ) {
        expectedTops.delete(el);
        return;
      }
      expectedTops.delete(el);
      mirror(el, el === retelling ? original : retelling);
    };

    applyPadding();
    retelling.addEventListener("scroll", onScroll, { passive: true });
    original.addEventListener("scroll", onScroll, { passive: true });

    // One pass per FRAME, not one per observation. `applyPadding` interleaves
    // `getBoundingClientRect` reads with `marginTop` writes once per anchor
    // (~4 forced reflows each, over the corpus's 35–93 anchors per chapter),
    // and a drag-resize of the window fires this observer for both panes on
    // every frame it moves. Cancel-and-reschedule keeps the LAST observation
    // of each frame, which is the only one whose measurements are still true.
    let rafId: number | null = null;
    const observer = new ResizeObserver(() => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        applyPadding();
      });
    });
    observer.observe(retelling);
    observer.observe(original);
    document.fonts?.ready.then(applyPadding).catch(() => undefined);

    return () => {
      retelling.removeEventListener("scroll", onScroll);
      original.removeEventListener("scroll", onScroll);
      observer.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      clearMargins(retelling);
      clearMargins(original);
      expectedTops.clear();
    };
  }, [
    retellingRef,
    originalRef,
    retellingModel,
    originalModel,
    anchors,
    enabled,
    retellingFontSize,
    originalFontSize,
    layoutKey,
  ]);
}
